import { AIProviderType } from './AIServiceFactory';
import { BaseConfig } from './types';

// 字段类型
export interface AIConfigField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'boolean';
  placeholder?: string;
  required?: boolean;
  defaultValue: string | number | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{value: string, label: string}>;
  description?: string;
}

// AI提供商配置接口
export interface AIProviderConfig {
  name: string;
  fields: AIConfigField[];
}

// AI提供商选项列表（用于下拉选择）
export const aiProviderOptions = [
  { value: null, label: '未选择' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'azureopenai', label: 'Azure OpenAI' },
  { value: 'claude', label: 'Anthropic Claude' },
  { value: 'ollama', label: 'Ollama' },
  { value: 'vllm', label: 'vLLM' },
  { value: 'lmstudio', label: 'LM Studio' }
];

// 默认配置值
export const defaultConfigs: Record<string, BaseConfig> = {
  openai: {
    useStreaming: true,
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  },
  azureopenai: {
    useStreaming: true,
    apiVersion: '2023-05-15',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  },
  claude: {
    useStreaming: true,
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  },
  ollama: {
    useStreaming: true,
    model: 'llama3',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  },
  vllm: {
    useStreaming: true,
    model: 'default',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  },
  lmstudio: {
    useStreaming: true,
    model: 'default',
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1
  }
};

// 提供商UI配置定义
export const providerConfigs: Record<string, AIProviderConfig> = {
  openai: {
    name: "OpenAI",
    fields: [
      {
        id: "apiKey",
        label: "API密钥",
        type: "text",
        placeholder: "sk-...",
        required: true,
        defaultValue: ""
      },
      {
        id: "apiUrl",
        label: "API URL",
        type: "text",
        placeholder: "https://api.openai.com/v1",
        defaultValue: "https://api.openai.com/v1"
      },
      {
        id: "model",
        label: "模型",
        type: "select",
        options: [
          { value: "gpt-4o", label: "GPT-4o" },
          { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
          { value: "gpt-4", label: "GPT-4" },
          { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" }
        ],
        defaultValue: "gpt-4o"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  },
  azureopenai: {
    name: "Azure OpenAI",
    fields: [
      {
        id: "apiKey",
        label: "API密钥",
        type: "text",
        placeholder: "您的Azure OpenAI API密钥",
        required: true,
        defaultValue: ""
      },
      {
        id: "endpoint",
        label: "端点",
        type: "text",
        placeholder: "https://your-resource-name.openai.azure.com",
        required: true,
        defaultValue: ""
      },
      {
        id: "deploymentName",
        label: "部署名称",
        type: "text",
        placeholder: "您的Azure OpenAI部署名称",
        required: true,
        defaultValue: ""
      },
      {
        id: "apiVersion",
        label: "API版本",
        type: "text",
        placeholder: "2023-05-15",
        defaultValue: "2023-05-15"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  },
  claude: {
    name: "Anthropic Claude",
    fields: [
      {
        id: "apiKey",
        label: "API密钥",
        type: "text",
        placeholder: "您的Claude API密钥",
        required: true,
        defaultValue: ""
      },
      {
        id: "model",
        label: "模型",
        type: "select",
        options: [
          { value: "claude-3-opus-20240229", label: "Claude 3 Opus" },
          { value: "claude-3-sonnet-20240229", label: "Claude 3 Sonnet" },
          { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku" }
        ],
        defaultValue: "claude-3-opus-20240229"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  },
  ollama: {
    name: "Ollama",
    fields: [
      {
        id: "apiUrl",
        label: "API URL",
        type: "text",
        placeholder: "http://localhost:11434",
        defaultValue: "http://localhost:11434"
      },
      {
        id: "model",
        label: "模型",
        type: "text",
        placeholder: "llama3",
        defaultValue: "llama3"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  },
  vllm: {
    name: "vLLM",
    fields: [
      {
        id: "endpoint",
        label: "端点",
        type: "text",
        placeholder: "请输入vLLM API URL",
        defaultValue: "http://localhost:8000"
      },
      {
        id: "model",
        label: "模型",
        type: "text",
        placeholder: "模型名称",
        defaultValue: "default"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  },
  lmstudio: {
    name: "LM Studio",
    fields: [
      {
        id: "apiUrl",
        label: "API URL",
        type: "text",
        placeholder: "http://localhost:1234/v1",
        defaultValue: "http://localhost:1234/v1"
      },
      {
        id: "model",
        label: "模型",
        type: "text",
        placeholder: "默认本地模型",
        defaultValue: "default"
      },
      {
        id: "useStreaming",
        label: "使用流式响应",
        type: "boolean",
        defaultValue: true,
        description: "实时显示生成结果"
      },
      {
        id: "temperature",
        label: "温度",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 0.7,
        description: "值越高，生成的结果越随机多样"
      },
      {
        id: "maxTokens",
        label: "最大Token数",
        type: "number",
        min: 100,
        max: 8192,
        step: 1,
        defaultValue: 1000,
        description: "生成的最大文本长度"
      },
      {
        id: "topP",
        label: "Top P",
        type: "number",
        min: 0,
        max: 1,
        step: 0.1,
        defaultValue: 1.0,
        description: "控制文本多样性，较低的值会使输出更确定"
      }
    ]
  }
};

// 获取默认配置
export function getDefaultConfig(provider: AIProviderType): BaseConfig | null {
  if (!provider) return null;
  return defaultConfigs[provider] || null;
}

// 获取提供商配置
export function getProviderConfig(provider: AIProviderType): AIProviderConfig | null {
  if (!provider) return null;
  return providerConfigs[provider] || null;
} 